import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { LanguageService } from '../../../core/i18n/language.service';

/**
 * Route `data.breadcrumb` value. A string is a Transloco key translated at
 * render time; `{ param }` names a route parameter whose human-readable label
 * is looked up in the registry (see `setLabel`), falling back to the raw
 * parameter value — which for routes like `devices/:clientId` already is the
 * display identifier.
 */
export type BreadcrumbData = string | { param: string };

export interface Crumb {
  label: string;
  url: string;
}

/**
 * Derives the breadcrumb trail from the active route tree. Hierarchy comes
 * from nested route configs: every route node carrying `data.breadcrumb`
 * contributes one crumb, and the crumb's URL is the path accumulated up to
 * that node — so clicking a segment navigates to that level.
 *
 * Dynamic segments are named via `setLabel`, not resolvers: the page (or the
 * list row-click handler, before navigating) already holds the entity, so a
 * resolver would only duplicate the fetch and block navigation. The registry
 * is keyed by `param:value` and never cleared — it doubles as a tiny name
 * cache, so back/forward to a visited entity shows its name instantly.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly language = inject(LanguageService);

  private readonly navEnd = toSignal(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
  );

  private readonly labels = signal<Record<string, string>>({});

  readonly crumbs = computed<Crumb[]>(() => {
    this.navEnd(); // recompute on every navigation
    this.language.current(); // re-translate on language switch
    const labels = this.labels();

    const out: Crumb[] = [];
    let url = '';
    for (
      let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
      route;
      route = route.firstChild
    ) {
      const segment = route.url.map((s) => s.path).join('/');
      if (segment) {
        url += '/' + segment;
      }
      // Read the node's OWN config data, not the merged snapshot data —
      // empty-path children (e.g. a detail shell at '' under ':tenantId')
      // inherit parent data under the default 'emptyOnly' strategy and would
      // each repeat the parent's crumb.
      const breadcrumb = route.routeConfig?.data?.['breadcrumb'] as BreadcrumbData | undefined;
      if (!breadcrumb) {
        continue;
      }
      if (typeof breadcrumb === 'string') {
        out.push({ label: this.transloco.translate(breadcrumb), url });
      } else {
        const value = route.params[breadcrumb.param] ?? '';
        out.push({ label: labels[`${breadcrumb.param}:${value}`] ?? value, url });
      }
    }
    return out;
  });

  /**
   * Name a dynamic segment. Called by detail pages once their entity loads,
   * and by list pages at click time (before `router.navigate`) so normal
   * navigation never flashes a raw id.
   */
  setLabel(param: string, value: string, label: string): void {
    this.labels.update((m) => ({ ...m, [`${param}:${value}`]: label }));
  }
}
